import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle, MonitorSmartphone, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api from "../lib/apiClient";

// Phone-side landing for the QR handoff. Requires the user to already
// be signed in on this device — otherwise we route them to the auth
// screen first and come back via ?returnTo=.
const QRClaimPage = () => {
  const { handoffId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [info, setInfo] = useState(null); // {valid, status}
  const [stage, setStage] = useState("loading"); // loading | confirm | done | error
  const [errorMsg, setErrorMsg] = useState("");
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/auth/qr/info/${handoffId}`);
        setInfo(res.data);
        if (!res.data.valid) {
          setStage("error");
          setErrorMsg(res.data.status === "expired" ? "This QR code has expired." : "This QR code has already been used.");
        } else if (!user) {
          // Push them to login then back here.
          toast.message("Sign in on this phone first, then re-scan.");
          navigate(`/?returnTo=${encodeURIComponent(`/qr-claim/${handoffId}`)}`, { replace: true });
        } else {
          setStage("confirm");
        }
      } catch (err) {
        setStage("error");
        setErrorMsg(err.response?.data?.detail || "Couldn't load QR code info.");
      }
    })();
  }, [handoffId, user, navigate]);

  const confirm = async () => {
    setConfirming(true);
    try {
      await api.post(`/auth/qr/claim/${handoffId}`, {});
      setStage("done");
      toast.success("Desktop signed in. You can close this page.");
    } catch (err) {
      setStage("error");
      setErrorMsg(err.response?.data?.detail || "Couldn't sign in your desktop.");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black flex items-center justify-center px-4 py-12" data-testid="qr-claim-page">
      <div className="w-full max-w-md bg-gray-900/80 backdrop-blur border border-gray-800 rounded-2xl p-8">
        {stage === "loading" && (
          <div className="text-center" data-testid="qr-claim-loading">
            <Loader2 className="h-10 w-10 text-emerald-400 animate-spin mx-auto mb-3" />
            <p className="text-gray-300">Loading QR sign-in…</p>
          </div>
        )}

        {stage === "confirm" && user && (
          <div data-testid="qr-claim-confirm">
            <div className="flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-widest mb-3">
              <MonitorSmartphone className="h-4 w-4" /> Phone → Desktop
            </div>
            <h1 className="text-white text-2xl font-semibold mb-2">Sign in your desktop?</h1>
            <p className="text-gray-400 text-sm mb-6">
              Your desktop will be signed in as{" "}
              <span className="text-emerald-300 font-medium">@{user.username}</span>.
              Only do this if you're at your own computer.
            </p>
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 flex items-start gap-2.5 mb-5">
              <ShieldCheck className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
              <div className="text-xs text-gray-300 leading-relaxed">
                We never share your password. The desktop gets a fresh session token that you can sign out of anytime.
              </div>
            </div>
            <Button
              onClick={confirm}
              disabled={confirming}
              className="w-full bg-emerald-600 hover:bg-emerald-700 mb-3"
              data-testid="qr-claim-confirm-btn"
            >
              {confirming ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Yes — sign in my desktop
            </Button>
            <Link to="/" className="block">
              <Button variant="outline" className="w-full border-gray-700 text-gray-300 hover:bg-gray-800" data-testid="qr-claim-cancel-btn">
                Cancel
              </Button>
            </Link>
          </div>
        )}

        {stage === "done" && (
          <div className="text-center" data-testid="qr-claim-done">
            <CheckCircle2 className="h-14 w-14 text-emerald-400 mx-auto mb-3" />
            <h1 className="text-white text-xl font-semibold mb-2">Desktop signed in</h1>
            <p className="text-gray-400 text-sm mb-6">You can close this tab — your desktop is ready.</p>
            <Link to="/" className="block">
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700">Done</Button>
            </Link>
          </div>
        )}

        {stage === "error" && (
          <div className="text-center" data-testid="qr-claim-error">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
            <h1 className="text-white text-xl font-semibold mb-2">QR sign-in failed</h1>
            <p className="text-gray-400 text-sm mb-6">{errorMsg}</p>
            <Link to="/" className="block">
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700">Back to home</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRClaimPage;
